import { EventType, NativeInteractionType, } from '@replayops/rrweb-schema';
/**
 * Renders a live native stream for operator co-watching — the mobile
 * counterpart of {@link LivePlayer}, with the same small surface so the Live
 * screen can hold either without caring which.
 *
 * There is no DOM to reconstruct and no snapshot to wait for: a frame is a
 * complete picture, so the first one that arrives can be drawn immediately.
 * That also means a dropped frame costs one stale moment rather than
 * desynchronising everything after it, which is the failure mode a mutation
 * stream has.
 */
/** A touch stays visible this long after it happened. */
const TOUCH_FADE_MS = 700;
export class NativeLivePlayer {
    root;
    canvas;
    touches = [];
    current;
    dims = { width: 0, height: 0 };
    onResize;
    raf = 0;
    destroyed = false;
    constructor(root) {
        this.root = root;
        this.canvas = document.createElement('canvas');
        this.canvas.style.cssText = 'width:100%;height:100%;object-fit:contain;display:block;background:#000';
        this.root.innerHTML = '';
        this.root.appendChild(this.canvas);
    }
    /** Notified with the device's screen size for scaling, like the web player. */
    onDimensions(cb) {
        this.onResize = cb;
        if (this.dims.width)
            cb(this.dims.width, this.dims.height);
    }
    addEvent(e) {
        if (this.destroyed)
            return;
        if (e.type === EventType.NativeFrame) {
            const frame = e.data;
            if (frame.width && frame.height && frame.width !== this.dims.width) {
                this.dims = { width: frame.width, height: frame.height };
                this.canvas.width = frame.width;
                this.canvas.height = frame.height;
                this.onResize?.(frame.width, frame.height);
            }
            const img = new Image();
            img.onload = () => {
                // A frame that finished decoding after a newer one arrived is stale;
                // drawing it would flick the operator's view backwards.
                if (this.current === img)
                    this.paint();
            };
            img.src = frame.image;
            this.current = img;
            return;
        }
        if (e.type === EventType.NativeInteraction) {
            const d = e.data;
            if (d.kind === NativeInteractionType.ScreenChange)
                return;
            if (typeof d.x !== 'number' || typeof d.y !== 'number')
                return;
            this.touches.push({ at: performance.now(), data: d });
            this.animate();
        }
    }
    destroy() {
        this.destroyed = true;
        cancelAnimationFrame(this.raf);
        this.touches = [];
        this.canvas.remove();
    }
    // ── drawing ──
    paint() {
        const ctx = this.canvas.getContext('2d');
        if (!ctx || !this.current)
            return;
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        if (this.current.complete && this.current.naturalWidth > 0) {
            ctx.drawImage(this.current, 0, 0, this.canvas.width, this.canvas.height);
        }
        this.drawTouches(ctx);
    }
    /** Keeps repainting while a touch is still fading, then stops. */
    animate() {
        if (this.raf)
            return;
        const step = () => {
            this.raf = 0;
            if (this.destroyed)
                return;
            const now = performance.now();
            this.touches = this.touches.filter((t) => now - t.at <= TOUCH_FADE_MS);
            this.paint();
            if (this.touches.length) {
                this.raf = requestAnimationFrame(step);
            }
        };
        this.raf = requestAnimationFrame(step);
    }
    drawTouches(ctx) {
        const now = performance.now();
        for (const t of this.touches) {
            const fade = 1 - (now - t.at) / TOUCH_FADE_MS;
            if (fade <= 0)
                continue;
            const { x = 0, y = 0, toX, toY } = t.data;
            if (toX != null && toY != null) {
                ctx.strokeStyle = `rgba(225,29,72,${fade})`;
                ctx.lineWidth = 4;
                ctx.beginPath();
                ctx.moveTo(x, y);
                ctx.lineTo(toX, toY);
                ctx.stroke();
            }
            ctx.beginPath();
            ctx.arc(x, y, 18 + (1 - fade) * 22, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(225,29,72,${0.25 * fade})`;
            ctx.fill();
            ctx.strokeStyle = `rgba(225,29,72,${fade})`;
            ctx.lineWidth = 3;
            ctx.stroke();
        }
    }
}
//# sourceMappingURL=nativeLivePlayer.js.map